import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import LearnHubGrid, { LearnGroup } from '@comp/learn/learn-hub-grid'
import FinalCta from '@comp/home/final-cta'

interface HubContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  groups: LearnGroup[]
}

const content: Record<'en' | 'tr', HubContent> = {
  en: {
    metaTitle: 'Learn Monofor',
    metaDescription:
      'Walkthroughs, deep-dive guides, and curated docs, organized by what you are trying to accomplish, not by product. Featured how-tos plus deep-links into help.monofor.com.',
    heroEyebrow: 'Learn',
    heroTitle: ['Walkthroughs.', 'Deep dives. Real docs.'],
    heroBody:
      'Concise how-to landings for the things teams ask us to demo most, plus curated deep-links into the full help center, organized by what you are trying to accomplish.',
    badges: [
      'Featured walkthroughs',
      'Deep-link to help.monofor.com',
      'Topic-clustered',
    ],
    primaryCta: 'Browse featured how-tos ↓',
    secondaryCta: 'Open the full docs',
    groups: [
      {
        category: 'Featured how-tos',
        intro: 'Fast walkthroughs of the things teams most often ask us to demo.',
        entries: [
          { label: 'SSO into AWS with SAML', description: 'Federate IAM and IAM Identity Center with Monosign.', href: '/learn/sso-aws-saml', minutes: 6 },
          { label: 'MFA on your VPN', description: 'Add Monosign MFA to Fortigate, Palo Alto, or Pulse Secure.', href: '/learn/mfa-on-vpn', minutes: 5 },
          { label: 'Windows passwordless login', description: 'Replace the Windows password prompt with Monofor Identity.', href: '/learn/windows-passwordless-login', minutes: 7 },
          { label: 'Get started with the REST API', description: 'Authenticate, list users, push a change.', href: '/learn/rest-api-getting-started', minutes: 8 },
          { label: 'Sync Active Directory', description: 'Connect AD as a Monosign identity source.', href: '/learn/active-directory-sync', minutes: 4 },
          { label: 'Export audit logs to your SIEM', description: 'Stream Monosign audits into Splunk, Sentinel, or ELK.', href: '/learn/audit-log-siem-export', minutes: 5 },
          { label: 'Migrate from ADFS', description: 'Retire the farm app by app; AD stays the source of truth.', href: '/learn/migrate-from-adfs', minutes: 7 },
          { label: 'Google Cloud console SSO', description: 'Workforce Identity Federation: console and gcloud with Monosign identities.', href: '/learn/gcp-workforce-identity-federation', minutes: 6 },
          { label: 'Keyless GCP for workloads', description: 'Workload Identity Federation: no service account keys to leak.', href: '/learn/gcp-workload-identity-federation', minutes: 6 },
          { label: 'Silent Kerberos SSO', description: 'Zero-click sign-in for domain-joined desktops via SPNEGO.', href: '/learn/kerberos-sso-setup', minutes: 5 },
          { label: 'LDAP Gateway for legacy apps', description: 'VPNs, NAS, vSphere keep speaking LDAP; the directory becomes Monosign.', href: '/learn/ldap-gateway-legacy-apps', minutes: 6 },
          { label: 'Migrate from Okta', description: 'Parallel-run both IdPs with a per-app cutover.', href: '/learn/migrate-from-okta', minutes: 8 },
          { label: 'OT vendor access', description: 'Brokered, time-boxed vendor sessions for plants and OT networks, no inbound VPN.', href: '/learn/ot-vendor-access', minutes: 6 },
        ],
      },
      {
        category: 'By topic: sign-on',
        intro: 'Authentication, federation, and access surfaces.',
        entries: [
          { label: 'SAML 2.0 integrations', description: 'The full list of SAML-supported apps and how to wire them.', href: 'https://help.monofor.com/monosign/saml-2.0-integrations', external: true },
          { label: 'OpenID Connect integrations', description: 'OIDC discovery, JWKS, and supported providers.', href: 'https://help.monofor.com/monosign/openid-oidc-integrations', external: true },
          { label: 'RADIUS configuration', description: 'Use Monosign as a RADIUS server for legacy infrastructure.', href: 'https://help.monofor.com/monosign/radius', external: true },
          { label: 'Kerberos authentication', description: 'Windows-domain SSO with Kerberos.', href: 'https://help.monofor.com/monosign/kerberos-authentication', external: true },
          { label: 'Browser-based sign-in', description: 'Apps without a federation API, covered.', href: 'https://help.monofor.com/monosign/browser-based-sign-in', external: true },
          { label: 'API-based sign-in', description: 'Service-account authentication flow.', href: 'https://help.monofor.com/monosign/api-based-sign-in', external: true },
        ],
      },
      {
        category: 'By topic: directories & lifecycle',
        intro: 'Identity sources, sync, and joiner / mover / leaver automation.',
        entries: [
          { label: 'Directory sources overview', description: 'AD, LDAP, OUD, WebAPI configurations.', href: 'https://help.monofor.com/monosign/configuration-directory-sources', external: true },
          { label: 'Active Directory source', description: 'AD as the canonical Monosign source.', href: 'https://help.monofor.com/monosign/configuration-directory-sources-ad', external: true },
          { label: 'WebAPI source', description: 'In-house systems as identity sources.', href: 'https://help.monofor.com/monosign/configuration-directory-sources-webapi', external: true },
          { label: 'Identity lifecycle (Monosync)', description: 'JML automation across every connected app.', href: 'https://help.monofor.com/monosync/identity-lifecycle-management', external: true },
          { label: 'Workflows and flows', description: 'Approval, provisioning, and request flows.', href: 'https://help.monofor.com/monosign/workflows', external: true },
          { label: 'Access certifications', description: 'Run access reviews on a cadence.', href: 'https://help.monofor.com/monosign/access-certification', external: true },
        ],
      },
      {
        category: 'By topic: privileged access',
        intro: 'Monopam vault, brokered access, and session recording.',
        entries: [
          { label: 'Monopam overview', description: 'Architecture and core concepts.', href: 'https://help.monofor.com/monopam/overview', external: true },
          { label: 'Vault management', description: 'Onboard, rotate, and govern secrets.', href: 'https://help.monofor.com/monopam/vault', external: true },
          { label: 'Access automation', description: 'Brokered credential injection.', href: 'https://help.monofor.com/monopam/access-automation', external: true },
          { label: 'Session recordings', description: 'Capture, search, and export.', href: 'https://help.monofor.com/monopam/recordings', external: true },
          { label: 'Connection through web UI', description: 'Browser-based privileged access.', href: 'https://help.monofor.com/monopam/connection-through-web-ui', external: true },
          { label: 'Monopam encryption methodology', description: 'How secrets are protected at rest and in transit.', href: 'https://help.monofor.com/monopam/what-encryption-methodology-that-monopam-use', external: true },
        ],
      },
      {
        category: 'By topic: APIs and integration',
        intro: 'Build on top of Monofor.',
        entries: [
          { label: 'REST API reference', description: 'Endpoints, auth, conventions.', href: 'https://help.monofor.com/monosign/rest-api', external: true },
          { label: 'OAuth 2 integration', description: 'Wire OAuth flows into your apps.', href: 'https://help.monofor.com/monosign/oauth-integration', external: true },
          { label: 'Custom REST application', description: 'Add in-house apps without a federation API.', href: 'https://help.monofor.com/monosign/custom-application-rest-api-integration', external: true },
          { label: 'Custom OIDC application', description: 'Register an OIDC client.', href: 'https://help.monofor.com/monosign/custom-openid-oidc-application', external: true },
          { label: 'Monosync API objects', description: 'Create and update objects via API.', href: 'https://help.monofor.com/monosync/how-to-create-update-an-object-on-monosync-using-m', external: true },
          { label: 'PowerShell automation (Monosync)', description: 'Script Monosync from PowerShell.', href: 'https://help.monofor.com/monosync/powershell', external: true },
        ],
      },
    ],
  },
  tr: {
    metaTitle: "Monofor'u öğrenin",
    metaDescription:
      "Rehberler, derinlemesine kılavuzlar ve derlenmiş dokümanlar: ürüne göre değil, yapmak istediğiniz işe göre düzenlendi. Öne çıkan rehberler ve help.monofor.com'a derin bağlantılar.",
    heroEyebrow: 'Öğrenin',
    heroTitle: ['Adım adım rehberler.', 'Derin dalışlar. Gerçek dokümanlar.'],
    heroBody:
      'Ekiplerin bizden en sık demo istediği konular için özlü rehber sayfaları ve yardım merkezinin tamamına açılan derlenmiş bağlantılar; ürüne göre değil, yapmak istediğiniz işe göre düzenlendi.',
    badges: [
      'Öne çıkan rehberler',
      "help.monofor.com'a derin bağlantılar",
      'Konuya göre kümelenmiş',
    ],
    primaryCta: 'Öne çıkan rehberlere göz atın ↓',
    secondaryCta: 'Dokümantasyonu açın',
    groups: [
      {
        category: 'Öne çıkan rehberler',
        intro: 'Ekiplerin bizden en sık demo istediği konuların hızlı anlatımları.',
        entries: [
          { label: "SAML ile AWS'ye SSO", description: "IAM ve IAM Identity Center'ı Monosign ile federasyona alın.", href: '/learn/sso-aws-saml', minutes: 6 },
          { label: "VPN'inizde MFA", description: "Fortigate, Palo Alto veya Pulse Secure'a Monosign MFA ekleyin.", href: '/learn/mfa-on-vpn', minutes: 5 },
          { label: 'Windows parolasız giriş', description: 'Windows parola ekranını Monofor Identity ile değiştirin.', href: '/learn/windows-passwordless-login', minutes: 7 },
          { label: "REST API'ye başlangıç", description: 'Kimlik doğrulayın, kullanıcıları listeleyin, bir değişiklik gönderin.', href: '/learn/rest-api-getting-started', minutes: 8 },
          { label: 'Active Directory senkronizasyonu', description: "AD'yi Monosign kimlik kaynağı olarak bağlayın.", href: '/learn/active-directory-sync', minutes: 4 },
          { label: "Denetim loglarını SIEM'inize aktarın", description: "Monosign denetimlerini Splunk, Sentinel veya ELK'ya aktarın.", href: '/learn/audit-log-siem-export', minutes: 5 },
          { label: "ADFS'ten geçiş", description: 'Çiftliği uygulama uygulama emekliye ayırın; AD doğruluk kaynağı kalır.', href: '/learn/migrate-from-adfs', minutes: 7 },
          { label: 'Google Cloud konsoluna SSO', description: 'Workforce Identity Federation: konsol ve gcloud, Monosign kimlikleriyle.', href: '/learn/gcp-workforce-identity-federation', minutes: 6 },
          { label: "Workload'lar için anahtarsız GCP", description: 'Workload Identity Federation: sızacak servis hesabı anahtarı yok.', href: '/learn/gcp-workload-identity-federation', minutes: 6 },
          { label: 'Sessiz Kerberos SSO', description: "Domain'e bağlı masaüstlerine SPNEGO ile sıfır tık giriş.", href: '/learn/kerberos-sso-setup', minutes: 5 },
          { label: 'Legacy uygulamalara LDAP Gateway', description: "VPN, NAS, vSphere LDAP konuşmaya devam eder; dizin Monosign olur.", href: '/learn/ldap-gateway-legacy-apps', minutes: 6 },
          { label: "Okta'dan geçiş", description: "İki IdP'yi paralel çalıştırın, uygulama bazında geçin.", href: '/learn/migrate-from-okta', minutes: 8 },
          { label: 'OT tedarikçi erişimi', description: 'Fabrikalar ve OT ağları için aracılı, süreli tedarikçi oturumları; gelen VPN yok.', href: '/learn/ot-vendor-access', minutes: 6 },
        ],
      },
      {
        category: 'Konuya göre: oturum açma',
        intro: 'Kimlik doğrulama, federasyon ve erişim yüzeyleri.',
        entries: [
          { label: 'SAML 2.0 entegrasyonları', description: 'SAML destekli uygulamaların tam listesi ve bağlama adımları.', href: 'https://help.monofor.com/monosign/saml-2.0-integrations', external: true },
          { label: 'OpenID Connect entegrasyonları', description: 'OIDC discovery, JWKS ve desteklenen sağlayıcılar.', href: 'https://help.monofor.com/monosign/openid-oidc-integrations', external: true },
          { label: 'RADIUS yapılandırması', description: "Eski altyapılar için Monosign'ı RADIUS sunucusu olarak kullanın.", href: 'https://help.monofor.com/monosign/radius', external: true },
          { label: 'Kerberos kimlik doğrulaması', description: 'Kerberos ile Windows domain SSO.', href: 'https://help.monofor.com/monosign/kerberos-authentication', external: true },
          { label: 'Tarayıcı tabanlı oturum açma', description: "Federasyon API'si olmayan uygulamalar da kapsanır.", href: 'https://help.monofor.com/monosign/browser-based-sign-in', external: true },
          { label: 'API tabanlı oturum açma', description: 'Servis hesabı kimlik doğrulama akışı.', href: 'https://help.monofor.com/monosign/api-based-sign-in', external: true },
        ],
      },
      {
        category: 'Konuya göre: dizinler ve yaşam döngüsü',
        intro: 'Kimlik kaynakları, senkronizasyon ve işe giriş / geçiş / ayrılış otomasyonu.',
        entries: [
          { label: 'Dizin kaynaklarına genel bakış', description: 'AD, LDAP, OUD, WebAPI yapılandırmaları.', href: 'https://help.monofor.com/monosign/configuration-directory-sources', external: true },
          { label: 'Active Directory kaynağı', description: 'Kanonik Monosign kaynağı olarak AD.', href: 'https://help.monofor.com/monosign/configuration-directory-sources-ad', external: true },
          { label: 'WebAPI kaynağı', description: 'Kurum içi sistemler kimlik kaynağı olarak.', href: 'https://help.monofor.com/monosign/configuration-directory-sources-webapi', external: true },
          { label: 'Kimlik yaşam döngüsü (Monosync)', description: 'Bağlı tüm uygulamalarda JML otomasyonu.', href: 'https://help.monofor.com/monosync/identity-lifecycle-management', external: true },
          { label: 'İş akışları ve akışlar', description: 'Onay, sağlama ve talep akışları.', href: 'https://help.monofor.com/monosign/workflows', external: true },
          { label: 'Erişim sertifikasyonları', description: 'Erişim gözden geçirmelerini düzenli aralıklarla yürütün.', href: 'https://help.monofor.com/monosign/access-certification', external: true },
        ],
      },
      {
        category: 'Konuya göre: ayrıcalıklı erişim',
        intro: 'Monopam kasası, aracılı erişim ve oturum kaydı.',
        entries: [
          { label: 'Monopam genel bakış', description: 'Mimari ve temel kavramlar.', href: 'https://help.monofor.com/monopam/overview', external: true },
          { label: 'Kasa yönetimi', description: 'Gizli bilgileri ekleyin, rotasyona alın ve yönetin.', href: 'https://help.monofor.com/monopam/vault', external: true },
          { label: 'Erişim otomasyonu', description: 'Aracılı kimlik bilgisi enjeksiyonu.', href: 'https://help.monofor.com/monopam/access-automation', external: true },
          { label: 'Oturum kayıtları', description: 'Kaydedin, arayın ve dışa aktarın.', href: 'https://help.monofor.com/monopam/recordings', external: true },
          { label: 'Web arayüzü üzerinden bağlantı', description: 'Tarayıcı tabanlı ayrıcalıklı erişim.', href: 'https://help.monofor.com/monopam/connection-through-web-ui', external: true },
          { label: 'Monopam şifreleme metodolojisi', description: 'Gizli bilgiler bekleme ve aktarım sırasında nasıl korunur.', href: 'https://help.monofor.com/monopam/what-encryption-methodology-that-monopam-use', external: true },
        ],
      },
      {
        category: "Konuya göre: API'ler ve entegrasyon",
        intro: 'Monofor üzerine inşa edin.',
        entries: [
          { label: 'REST API referansı', description: "Endpoint'ler, kimlik doğrulama, kurallar.", href: 'https://help.monofor.com/monosign/rest-api', external: true },
          { label: 'OAuth 2 entegrasyonu', description: 'OAuth akışlarını uygulamalarınıza bağlayın.', href: 'https://help.monofor.com/monosign/oauth-integration', external: true },
          { label: 'Özel REST uygulaması', description: "Federasyon API'si olmayan kurum içi uygulamaları ekleyin.", href: 'https://help.monofor.com/monosign/custom-application-rest-api-integration', external: true },
          { label: 'Özel OIDC uygulaması', description: 'Bir OIDC istemcisi kaydedin.', href: 'https://help.monofor.com/monosign/custom-openid-oidc-application', external: true },
          { label: 'Monosync API nesneleri', description: 'API üzerinden nesne oluşturun ve güncelleyin.', href: 'https://help.monofor.com/monosync/how-to-create-update-an-object-on-monosync-using-m', external: true },
          { label: 'PowerShell otomasyonu (Monosync)', description: "Monosync'i PowerShell'den yönetin.", href: 'https://help.monofor.com/monosync/powershell', external: true },
        ],
      },
    ],
  },
}

type Props = { locale: string }

export default function LearnHubPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '#featured' }}
          secondaryCta={{ label: c.secondaryCta, href: 'https://help.monofor.com' }}
        />
        <div id="featured" />
        <LearnHubGrid groups={c.groups} />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
